import backend, {LOOM_API} from 'utils/backend/loom/api_library';

import queryString from 'query-string';

import {
  adaptLoomClientRoster,
  adaptLoomShift,
  adaptLoomShiftRange,
  toLoomClientRosterQuery,
  toLoomShiftRangeQuery,
} from './schedule_adapter';

const loomApi = () => {
  if (!LOOM_API) {
    throw new Error('REACT_APP_LOOM_API must be configured before Loom schedule reads can be enabled.');
  }

  return LOOM_API.replace(/\/$/, '');
};

const loadRange = async (rnField, rn, start, end, includeDeactivated) => {
  const query = queryString.stringify(
      toLoomShiftRangeQuery(rnField, rn, start, end, includeDeactivated),
      {skipNull: true, skipEmptyString: true},
  );
  const response = await backend.get(`${loomApi()}/api/careos/shifts?${query}`);
  return adaptLoomShiftRange(response);
};

const loadClientRange = (clientRn, start, end, options = {}) => {
  return loadRange('clientRn', clientRn, start, end, options.includeDeactivated);
};

const loadCaregiverRange = (caregiverRn, start, end, options = {}) => {
  return loadRange('caregiverRn', caregiverRn, start, end, options.includeDeactivated);
};

// The calendar's left column needs the roster to come from the same dataset as the cells:
// Loom dev mirrors legacy prod while the Client API serves beta, so a legacy roster produces
// RNs Loom has never heard of and every week renders empty.
const loadClients = async (filters, sort, cursor) => {
  const query = queryString.stringify(
      toLoomClientRosterQuery(filters, sort, cursor),
      {skipNull: true, skipEmptyString: true},
  );
  const response = await backend.get(`${loomApi()}/api/careos/clients?${query}`);
  return adaptLoomClientRoster(response);
};

// The detail pages ask by month while Loom's endpoint takes an ISO range, so this spans the
// months requested. A day of padding on each end keeps a late-evening shift on a month boundary
// from falling outside the UTC window, since Loom stores no per-event zone to align to.
const monthsToIsoRange = (byMonths) => {
  const ordinals = byMonths.map(({year, month}) => Number(year) * 12 + (Number(month) - 1));
  const first = Math.min(...ordinals);
  const last = Math.max(...ordinals);
  const start = Date.UTC(Math.floor(first / 12), first % 12, 1) - 86400000;
  const end = Date.UTC(Math.floor(last / 12), (last % 12) + 1, 1) + 86400000;

  return {start: new Date(start).toISOString(), end: new Date(end).toISOString()};
};

const loadMonths = (rnField) => async (rn, byMonths, loadCaregiverUpdates, options = {}) => {
  if (!byMonths?.length) {
    return {events: []};
  }

  const {start, end} = monthsToIsoRange(byMonths);
  return loadRange(rnField, rn, start, end, options.includeDeactivated);
};

const loadClientMonths = loadMonths('clientRn');
const loadCaregiverMonths = loadMonths('caregiverRn');

const loadEvent = async (eventRn) => {
  const response = await backend.get(`${loomApi()}/api/careos/shifts/${encodeURIComponent(eventRn)}`);
  return {event: adaptLoomShift(response)};
};

export default {
  loadClientRange,
  loadCaregiverRange,
  loadClientMonths,
  loadCaregiverMonths,
  loadClients,
  loadEvent,
};
